// ============================================
// ALMACEN/ESCANERSERIES.JS — escaneo CONTINUO de series (ALMACEN v3, 2026-10-21, A4)
// ============================================
// Dos caminos que terminan en el mismo sitio:
//   1) LECTOR DE MANO: funciona como teclado, escribe la serie y manda Enter -> el campo de abajo la recibe.
//   2) CAMARA DEL CELULAR: zxing (ya en el proyecto, components/Escaner.js) leyendo sin parar; cada codigo nuevo se
//      valida y se agrega a la lista sin cerrar la camara (para 10 camaras no se abre 10 veces).
// Cada lectura pasa por `validar(serie)` (async): devuelve null si sirve o el motivo del rechazo (EAN/modelo/repetida/ya
// existe). Lo rechazado se muestra en rojo y NO entra a la lista. Props:
//   series: string[]          lista actual (controlada por el padre)
//   onAgregar(serie)          la serie ya validada
//   onQuitar(serie)
//   validar(serie) -> Promise<string|null>
//   tope: number|null         cuantas caben (p.ej. la cantidad de la linea); null = sin tope
//   etiqueta, compacto
import React, { useCallback, useEffect, useRef, useState } from 'react';

const PP = "'Poppins', sans-serif";
const C = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#8a8f95', verde: '#1D9E75', rojo: '#e05c5c', ambar: '#b45309', fondo: '#f3f4f4' };

export default function EscanerSeries({ series = [], onAgregar, onQuitar, validar, tope = null, etiqueta = 'Series', compacto = false }) {
  const [manual, setManual] = useState('');
  const [camara, setCamara] = useState(false);
  const [ultimo, setUltimo] = useState(null); // { serie, ok, motivo }
  const [ocupado, setOcupado] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const recientes = useRef(new Map()); // serie -> timestamp (evita leer la misma 20 veces por segundo)
  const lleno = tope != null && series.length >= tope;

  const procesar = useCallback(async (texto) => {
    const s = String(texto || '').trim();
    if (!s || ocupado) return;
    const ahora = Date.now();
    const antes = recientes.current.get(s.toUpperCase());
    if (antes && ahora - antes < 2500) return; // misma lectura repetida por la camara
    recientes.current.set(s.toUpperCase(), ahora);
    setOcupado(true);
    try {
      if (tope != null && series.length >= tope) { setUltimo({ serie: s, ok: false, motivo: `Ya van ${series.length} de ${tope}: no caben más.` }); return; }
      if (series.some(x => x.toUpperCase() === s.toUpperCase())) { setUltimo({ serie: s, ok: false, motivo: 'Esa serie ya está en la lista.' }); return; }
      const motivo = validar ? await validar(s) : null;
      if (motivo) { setUltimo({ serie: s, ok: false, motivo }); if (navigator.vibrate) navigator.vibrate([60, 40, 60]); return; }
      onAgregar(s);
      setUltimo({ serie: s, ok: true });
      if (navigator.vibrate) navigator.vibrate(80);
    } finally { setOcupado(false); }
  }, [ocupado, series, tope, validar, onAgregar]);

  const enviarManual = () => { const s = manual.trim(); if (!s) return; setManual(''); procesar(s); };

  // Camara continua (zxing). Se carga solo al abrir: no pesa si nadie la usa.
  useEffect(() => {
    if (!camara) return undefined;
    let vivo = true;
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/library');
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        const devices = await reader.listVideoInputDevices();
        if (!devices.length) { setUltimo({ serie: '', ok: false, motivo: 'No se detectó cámara. Usa el lector o escribe la serie.' }); setCamara(false); return; }
        const trasera = devices.find(d => /back|trasera|rear|environment/i.test(d.label || ''));
        const id = trasera ? trasera.deviceId : devices[devices.length - 1].deviceId;
        reader.decodeFromVideoDevice(id, videoRef.current, (result) => {
          if (!vivo || !result) return;
          procesar(result.getText());
        });
      } catch (e) {
        if (vivo) { setUltimo({ serie: '', ok: false, motivo: 'No se pudo abrir la cámara. Usa el lector o escribe la serie.' }); setCamara(false); }
      }
    })();
    return () => { vivo = false; if (readerRef.current) { try { readerRef.current.reset(); } catch { /* nada */ } readerRef.current = null; } };
  }, [camara, procesar]);

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase' }}>🔢 {etiqueta}{tope != null ? ` · ${series.length} de ${tope}` : ` · ${series.length}`}</span>
        <button type="button" onClick={() => setCamara(v => !v)} disabled={lleno && !camara} style={{ padding: '6px 10px', minHeight: 36, borderRadius: 8, background: camara ? C.oscuro : '#fff', color: camara ? '#fff' : C.oscuro, border: `1px solid ${C.oscuro}`, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: PP, opacity: lleno && !camara ? 0.5 : 1 }}>{camara ? '⏹ Cerrar cámara' : '📷 Escanear con la cámara'}</button>
      </div>
      {camara && (
        <div style={{ position: 'relative', background: '#000', borderRadius: 12, overflow: 'hidden', aspectRatio: '4/3', maxHeight: compacto ? 200 : 280 }}>
          <video ref={videoRef} data-escaner="video" style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', height: '45%', border: '2px solid rgba(92,154,219,0.9)', borderRadius: 10 }} />
          <div style={{ position: 'absolute', bottom: 6, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 11, textShadow: '0 1px 2px #000' }}>Apunta a la etiqueta S/N · lee sin parar</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); enviarManual(); } }}
          placeholder={lleno ? 'Lista completa' : 'Lector de mano o escribe la serie y Enter'}
          disabled={lleno}
          data-escaner="entrada"
          autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false} enterKeyHint="done"
          style={{ flex: 1, minWidth: 0, padding: '10px 12px', minHeight: 44, borderRadius: 10, border: '1px solid #d8dde3', fontFamily: 'monospace', fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
        />
        <button type="button" onClick={enviarManual} disabled={!manual.trim() || lleno || ocupado} style={{ padding: '0 14px', minHeight: 44, borderRadius: 10, background: C.principal, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', fontFamily: PP, opacity: !manual.trim() || lleno ? 0.5 : 1 }}>Agregar</button>
      </div>
      {ultimo && (
        <div data-escaner="aviso" style={{ fontSize: 12, fontWeight: 600, borderRadius: 8, padding: '6px 10px', background: ultimo.ok ? '#e8faf5' : '#fde8e8', color: ultimo.ok ? C.verde : '#b02020' }}>
          {ultimo.ok ? `✅ ${ultimo.serie} agregada` : `⛔ ${ultimo.serie ? `${ultimo.serie}: ` : ''}${ultimo.motivo}`}
        </div>
      )}
      {series.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {series.map(s => (
            <span key={s} data-escaner="serie" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.fondo, borderRadius: 8, padding: '4px 8px', fontSize: 12.5, fontFamily: 'monospace', color: C.gris }}>
              {s}
              {onQuitar && <button type="button" onClick={() => onQuitar(s)} aria-label={`Quitar ${s}`} style={{ border: 'none', background: 'transparent', color: C.rojo, cursor: 'pointer', fontSize: 14, minWidth: 28, minHeight: 28, fontFamily: PP }}>×</button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
